import BaseModal from './BaseModal';
import s from './BaseModal.module.css';
import l from './LoginModal.module.css';
import c from './SaveContactsModal.module.css';
import { tryFetchWithAuth } from 'utils/fetchWithAuth';
import { useState, useEffect, useRef } from 'react';
import LoadingLogo from 'components/Logos/LoadingLogo';

const SaveContactsModal = ({ onClose, selectedContacts }) => {
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveMade, setSaveMade] = useState(false);
    const [contactsAddedCount, setContactsAddedCount] = useState(0);

    const [lists, setLists] = useState([]);
    const [listsLoading, setListsLoading] = useState(true);
    const [listComboOpen, setListComboOpen] = useState(false);
    const [listInputValue, setListInputValue] = useState('');
    const [selectedListId, setSelectedListId] = useState(null);
    const [selectedListName, setSelectedListName] = useState('');
    const [listActionError, setListActionError] = useState(null);
    const [createListLoading, setCreateListLoading] = useState(false);

    const comboRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setListsLoading(true);
            const { data, error } = await tryFetchWithAuth('/saved_contact_lists', {
                method: 'GET',
            });
            if (!cancelled) {
                if (!error && data?.lists) {
                    setLists(data.lists);
                } else {
                    setLists([]);
                }
                setListsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!listComboOpen) return undefined;
        const onDocMouseDown = (e) => {
            if (comboRef.current && !comboRef.current.contains(e.target)) {
                setListComboOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [listComboOpen]);

    const trimmedListQuery = listInputValue.trim();
    const listNameMatchesExisting = lists.some(
        (item) => item.name.toLowerCase() === trimmedListQuery.toLowerCase()
    );
    const showCreateOption =
        trimmedListQuery.length > 0 && !listNameMatchesExisting;

    const filteredLists = trimmedListQuery
        ? lists.filter((item) =>
              item.name.toLowerCase().includes(trimmedListQuery.toLowerCase())
          )
        : lists;

    const pickExistingList = (item) => {
        setSelectedListId(item.id);
        setSelectedListName(item.name);
        setListInputValue(item.name);
        setListComboOpen(false);
        setListActionError(null);
    };

    const pickCreateList = async () => {
        if (!trimmedListQuery || createListLoading) return;
        setCreateListLoading(true);
        setListActionError(null);
        const { data, error } = await tryFetchWithAuth('/create_list', {
            method: 'POST',
            body: JSON.stringify({ name: trimmedListQuery }),
        });
        setCreateListLoading(false);
        if (error) {
            setListActionError(
                typeof error === 'string' ? error : 'Could not create list.'
            );
            return;
        }
        const created = data;
        setLists((prev) =>
            [...prev, created].sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
        );
        setSelectedListId(created.id);
        setSelectedListName(created.name);
        setListInputValue(created.name);
        setListComboOpen(false);
    };

    const clearListSelection = () => {
        setSelectedListId(null);
        setSelectedListName('');
        setListInputValue('');
        setListComboOpen(false);
        setListActionError(null);
    };

    const handleListInputChange = (e) => {
        const v = e.target.value;
        setListInputValue(v);
        if (selectedListId && v !== selectedListName) {
            setSelectedListId(null);
            setSelectedListName('');
        }
        setListActionError(null);
    };

    const saveContacts = async () => {
        setSaveMade(true);
        setSaveLoading(true);
        setSaveError(null);

        const payload = { contactIds: selectedContacts };
        if (selectedListId != null) {
            payload.listId = selectedListId;
        }

        const { data, error } = await tryFetchWithAuth('/save_contacts', {
            method: 'POST',
            body: JSON.stringify(payload),
        });

        if (error) {
            setSaveError(error);
        } else {
            setContactsAddedCount(data?.contactsAddedCount ?? 0);
        }
        setSaveLoading(false);
    };

    const comboDisabled = saveLoading || listsLoading;

    return (
        <BaseModal height="28rem" width="28rem" onClose={onClose}>
            <div className={l.loginModalContainer}>
                {!saveLoading && !saveMade && (
                    <>
                        <div className={l.title}>
                            Save {selectedContacts.length}{' '}
                            {selectedContacts.length === 1 ? 'Contact' : 'Contacts'}
                        </div>
                        <div className={l.subtitle}>
                            This will use {selectedContacts.length}{' '}
                            {selectedContacts.length === 1 ? 'credit' : 'credits'}, and will
                            reveal and save all the{' '}
                            {selectedContacts.length === 1
                                ? "contact's"
                                : "contacts'"}{' '}
                            available email addresses and phone numbers. You can access and
                            export your saved contacts at any time by using the &quot;Saved&quot;
                            toggle in the filter box.
                        </div>

                        <div className={c.listSection}>
                            <div className={c.listLabel}>Add to a list (optional)</div>
                            <div className={c.listHint}>
                                Choose an existing list, or type a name and select{' '}
                                <strong>Create list …</strong> below.
                            </div>
                            <div className={c.comboWrap} ref={comboRef}>
                                <input
                                    type="text"
                                    className={c.comboInput}
                                    placeholder={
                                        listsLoading
                                            ? 'Loading lists…'
                                            : 'Search lists or type a new name…'
                                    }
                                    value={listInputValue}
                                    onChange={handleListInputChange}
                                    onFocus={() => setListComboOpen(true)}
                                    disabled={comboDisabled}
                                    autoComplete="off"
                                    aria-expanded={listComboOpen}
                                    aria-haspopup="listbox"
                                />
                                {listComboOpen && !listsLoading && (
                                    <div className={c.dropdown} role="listbox">
                                        {filteredLists.length === 0 && !showCreateOption && (
                                            <div className={c.dropdownEmpty}>
                                                No lists yet. Type a name below to create one.
                                            </div>
                                        )}
                                        {filteredLists.map((item) => (
                                            <div
                                                key={item.id}
                                                role="option"
                                                tabIndex={0}
                                                className={c.option}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => pickExistingList(item)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        pickExistingList(item);
                                                    }
                                                }}
                                            >
                                                {item.name}
                                            </div>
                                        ))}
                                        {showCreateOption && (
                                            <div
                                                role="option"
                                                tabIndex={0}
                                                className={`${c.option} ${c.optionCreate}`}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => pickCreateList()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        pickCreateList();
                                                    }
                                                }}
                                            >
                                                {createListLoading
                                                    ? 'Creating list…'
                                                    : `Create list "${trimmedListQuery}"`}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {listActionError && (
                                <div className={c.listError}>{listActionError}</div>
                            )}
                            {selectedListId != null && (
                                <button
                                    type="button"
                                    className={c.clearListBtn}
                                    onClick={clearListSelection}
                                >
                                    Clear list selection
                                </button>
                            )}
                        </div>

                        <div
                            className={`${s.blackButton} ${l.button}`}
                            onClick={saveContacts}
                        >
                            Save {selectedContacts.length}{' '}
                            {selectedContacts.length === 1 ? 'Contact' : 'Contacts'}
                        </div>
                    </>
                )}
                {saveLoading && (
                    <>
                        <LoadingLogo />
                    </>
                )}
                {saveMade && !saveLoading && saveError && (
                    <>
                        <div className={l.title}>Error Saving Contacts</div>
                        <div className={l.subtitle}>
                            Please refresh the page and try again!
                        </div>
                    </>
                )}
                {saveMade && !saveLoading && !saveError && (
                    <>
                        <div className={l.title}>
                            {contactsAddedCount}{' '}
                            {contactsAddedCount === 1 ? 'contact' : 'contacts'} saved!
                        </div>
                        <div className={l.subtitle}>
                            You can access and export your saved contacts at any time by using
                            the &quot;Saved&quot; toggle in the filter box.
                        </div>
                        <div
                            className={`${s.blackButton} ${l.button}`}
                            onClick={onClose}
                        >
                            OK
                        </div>
                    </>
                )}
            </div>
        </BaseModal>
    );
};

export default SaveContactsModal;
